import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import YouTube from "react-youtube";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { useNavigate } from "react-router-dom";
import { useVideo } from "../Contexts/VideosContext";
import { useAudio } from "../Contexts/AudiosContext";
import { pauseAudioForVideo, skipAudioResumeForHandoff } from "../utils/mediaMutex";

/** Styles for the global PiP overlay (injected once at app level) */
const pipOverlayStyles = `
  .video-pip-overlay {
    position: fixed;
    z-index: 1600;
    width: 320px;
    background: #fffaf0;
    border: 1px solid #ead7b1;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
    overflow: hidden;
    pointer-events: auto;
  }
  .video-pip-handle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 8px;
    background: linear-gradient(180deg, #fffaf1 0%, #fff4e2 100%);
    border-bottom: 1px solid #ead7b1;
    cursor: move;
    user-select: none;
    touch-action: none;
  }
  .video-pip-title {
    flex: 1;
    min-width: 0;
    font-size: 12px;
    font-weight: 600;
    color: #5c2513;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .video-pip-controls {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
  }
  .video-pip-frame {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #120008;
  }
  .video-pip-frame iframe,
  .video-pip-frame .youtube-frame {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }
  @media (max-width: 575.98px) {
    .video-pip-overlay { width: min(92vw, 320px); }
  }
`;

/** Default bottom-right placement for the floating PiP window */
const PIP_DEFAULT_BOTTOM = 20;
const PIP_DEFAULT_RIGHT = 20;
const PIP_WIDTH = 320;

/**
 * App-level floating picture-in-picture YouTube player.
 * Stays mounted across route changes so users can browse while watching.
 */
const VideoPiPOverlay: React.FC = () => {
  const navigate = useNavigate();
  const overlayRef = useRef<HTMLDivElement>(null);
  const pipPlayerRef = useRef<any>(null);

  const {
    isPiP,
    setIsPiP,
    videoId,
    setVideoId,
    playbackTime,
    setPlaybackTime,
    pipSlug,
    pipTitle,
    setPlayingVideo,
    setActiveMedia,
    playerRef,
  } = useVideo();

  const { refAudio, setPlayingAudio } = useAudio();

  const [position, setPosition] = useState<{ left: number; top: number } | null>(
    null
  );
  /** Keep latest position for drag-end logs without rebinding listeners */
  const positionRef = useRef<{ left: number; top: number } | null>(null);
  positionRef.current = position;

  const youtubeOptions = useMemo(
    () => ({
      height: "100%",
      width: "100%",
      playerVars: {
        autoplay: 1,
        playsinline: 1,
        controls: 1,
        rel: 0,
        modestbranding: 1,
      },
    }),
    []
  );

  /** Clamp overlay position so it stays inside the viewport */
  const clampPosition = useCallback((left: number, top: number) => {
    const node = overlayRef.current;
    const width = node?.offsetWidth || PIP_WIDTH;
    const height = node?.offsetHeight || 220;
    const maxLeft = Math.max(0, window.innerWidth - width);
    const maxTop = Math.max(0, window.innerHeight - height);
    return {
      left: Math.min(Math.max(0, left), maxLeft),
      top: Math.min(Math.max(0, top), maxTop),
    };
  }, []);

  /** Reset position when PiP opens */
  useEffect(() => {
    if (!isPiP || !videoId) return;
    // Wait a frame so overlayRef is measured after mount
    const frame = requestAnimationFrame(() => {
      const node = overlayRef.current;
      if (!node) return;
      const width = node.offsetWidth || PIP_WIDTH;
      const height = node.offsetHeight || 220;
      setPosition(
        clampPosition(
          window.innerWidth - width - PIP_DEFAULT_RIGHT,
          window.innerHeight - height - PIP_DEFAULT_BOTTOM
        )
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [isPiP, videoId, clampPosition]);

  /** Track playback time while PiP is active */
  useEffect(() => {
    if (!isPiP) return;

    const interval = setInterval(() => {
      const player = pipPlayerRef.current;
      if (player && typeof player.getCurrentTime === "function") {
        setPlaybackTime(player.getCurrentTime());
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPiP, setPlaybackTime]);

  /**
   * Drag by handle bar only (iframe steals mouse).
   * Listeners stay on document; do NOT depend on `position` or rebinding
   * will reset isDragging after the first setState and freeze movement.
   */
  useEffect(() => {
    if (!isPiP) return;

    const node = overlayRef.current;
    if (!node) return;

    const handle = node.querySelector(".video-pip-handle") as HTMLElement | null;
    if (!handle) return;

    let offsetX = 0;
    let offsetY = 0;
    let isDragging = false;
    let activePointerId: number | null = null;

    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging || e.pointerId !== activePointerId) return;
      e.preventDefault();
      const next = clampPosition(e.clientX - offsetX, e.clientY - offsetY);
      setPosition(next);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!isDragging || e.pointerId !== activePointerId) return;
      isDragging = false;
      activePointerId = null;

      try {
        handle.releasePointerCapture(e.pointerId);
      } catch {
        // capture may already be released
      }

      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);

      const logdetails: string[] = [];
      logdetails.push("action=pip_drag_end");
      const pos = positionRef.current;
      if (pos) {
        logdetails.push(`left=${Math.round(pos.left)}`);
        logdetails.push(`top=${Math.round(pos.top)}`);
      }
      console.log("VideoPiPOverlay ->", logdetails.join(" | "));
    };

    const onPointerDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest(".video-pip-controls")) return;
      // Only primary button / touch
      if (e.pointerType === "mouse" && e.button !== 0) return;

      isDragging = true;
      activePointerId = e.pointerId;
      const rect = node.getBoundingClientRect();
      offsetX = e.clientX - rect.left;
      offsetY = e.clientY - rect.top;

      handle.setPointerCapture(e.pointerId);
      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
      document.addEventListener("pointercancel", onPointerUp);
    };

    handle.addEventListener("pointerdown", onPointerDown);

    return () => {
      handle.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };
  }, [isPiP, clampPosition]);

  const handlePlayerReady = (event: any) => {
    const player = event.target;
    pipPlayerRef.current = player;
    playerRef.current = player;

    if (playbackTime > 0) {
      player.seekTo(playbackTime, true);
    }

    player.unMute();
    player.setVolume(100);

    // Video started last — stop any running audio
    pauseAudioForVideo({
      refAudio,
      setPlayingAudio,
      setActiveMedia,
    });

    player.playVideo();
    setPlayingVideo(true);
  };

  const handleClose = () => {
    const logdetails: string[] = [];
    logdetails.push("action=pip_close");

    if (pipPlayerRef.current && typeof pipPlayerRef.current.getCurrentTime === "function") {
      const time = pipPlayerRef.current.getCurrentTime();
      setPlaybackTime(time);
      logdetails.push(`playbackTime=${Math.round(time)}`);
    }

    if (pipPlayerRef.current && typeof pipPlayerRef.current.pauseVideo === "function") {
      pipPlayerRef.current.pauseVideo();
    }

    setIsPiP(false);
    setVideoId("");
    setPlayingVideo(false);
    setActiveMedia("none");
    pipPlayerRef.current = null;
    playerRef.current = null;

    console.log("VideoPiPOverlay ->", logdetails.join(" | "));
  };

  const handleExpand = () => {
    const logdetails: string[] = [];
    logdetails.push("action=pip_expand");
    // PiP is pausing so the full-page player can continue — do not resume audio.
    skipAudioResumeForHandoff();

    if (pipPlayerRef.current && typeof pipPlayerRef.current.getCurrentTime === "function") {
      const time = pipPlayerRef.current.getCurrentTime();
      setPlaybackTime(time);
      logdetails.push(`playbackTime=${Math.round(time)}`);
    }

    if (pipPlayerRef.current && typeof pipPlayerRef.current.pauseVideo === "function") {
      pipPlayerRef.current.pauseVideo();
    }

    setIsPiP(false);
    setPlayingVideo(false);
    pipPlayerRef.current = null;
    playerRef.current = null;

    if (pipSlug) {
      logdetails.push(`destination=/videos/${pipSlug}`);
      navigate(`/videos/${encodeURIComponent(pipSlug)}`, {
        state: { from: "VideoPiPOverlay", restorePlayback: true },
      });
    }

    console.log("VideoPiPOverlay ->", logdetails.join(" | "));
  };

  if (!isPiP || !videoId) {
    return null;
  }

  return (
    <>
      <style>{pipOverlayStyles}</style>
      <div
      ref={overlayRef}
      className="video-pip-overlay"
      style={
        position
          ? { left: position.left, top: position.top }
          : {
              right: PIP_DEFAULT_RIGHT,
              bottom: PIP_DEFAULT_BOTTOM,
            }
      }
      aria-label="Picture in picture video player"
    >
      <div className="video-pip-handle">
        <span className="video-pip-title" title={pipTitle}>
          {pipTitle || "Video"}
        </span>
        <div className="video-pip-controls">
          <IconButton
            size="small"
            onClick={handleExpand}
            aria-label="Expand video"
            sx={{ color: "#5c2513" }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close picture in picture"
            sx={{ color: "#c0392b" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="video-pip-frame">
        <YouTube
          key={videoId}
          className="youtube-frame"
          videoId={videoId}
          opts={youtubeOptions}
          onReady={handlePlayerReady}
          onPlay={() => {
            pauseAudioForVideo({
              refAudio,
              setPlayingAudio,
              setActiveMedia,
            });
            setPlayingVideo(true);
          }}
          onPause={() => setPlayingVideo(false)}
          onEnd={() => setPlayingVideo(false)}
        />
      </div>
    </div>
    </>
  );
};

export default VideoPiPOverlay;
